import { useState } from "react";

import { InformationIcon } from "@/assets/icons";
import { DatasetTypeIcon } from "@/components/dataset/datasetList/DatasetTypeIcon";
import MapMenuItemControl from "@/components/map/menuItemControl";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import Checkbox from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import Tooltip from "@/components/ui/tooltip";
import { ADVANCED_NORMALIZATION_ERROR_MESSAGES } from "@/constants/message";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import {
  type Dataset,
  DatasetAttributeSchema,
  WeightValueEntry
} from "@/types/dataset";
import { ScoringOptions, ScoringOptionsEnum } from "@/types/spatial";
import {
  getDatasetAttributeScoringId,
  getPrimaryPoiAttribute
} from "@/utils/datasetAttributes";
import {
  getAdvancedNormalizationErrorMessage,
  getIsIncludedScoring,
  getNegativeImpactValue,
  getWeightString,
  isLogMeanInvalid,
  isLogStdDevInvalid
} from "@/utils/weightValues";

import { twMerge } from "tailwind-merge";

import AdvancedSettingInputs from "./AdvancedSettingInputs";
import { InputWithClear } from "./InputWithClear";
import { WeightSettingsAction } from "./weightSettingsReducer";

// Shared column template for the attribute table: name | weight | % |
// negative impact | advanced settings (mean/stdv) | include toggle.
const ATTRIBUTE_GRID =
  "grid items-center gap-2 grid-cols-[minmax(90px,1fr)_140px_56px_100px_minmax(220px,1.8fr)_44px]";

interface WeightPercentageProps {
  value?: string;
  plain?: boolean;
}

const WeightPercentage = ({
  value = "0",
  plain = false
}: WeightPercentageProps) => {
  return (
    <div
      className={twMerge(
        "max-w-[60px] flex flex-row items-center justify-center h-fit text-txtBody px-2 py-1 text-xs font-semibold",
        !plain && "bg-[#EEE] rounded-lg border border-gainsboro"
      )}
      aria-label="weight-percentage"
    >
      {value}%
    </div>
  );
};

const AdvancedValidationMessages = ({
  messages,
  className
}: {
  messages: string[];
  className?: string;
}) => {
  const message =
    messages.find(
      (validationMessage) =>
        validationMessage ===
        ADVANCED_NORMALIZATION_ERROR_MESSAGES.BOTH_REQUIRED
    ) ?? messages[0];
  if (!message) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={twMerge("text-[10px] text-red-600", className)}
    >
      <p>*{message}</p>
    </div>
  );
};

interface DatasetAttributeItemProps {
  dataset: Dataset;
  datasetAttributesWeights: Record<string, WeightValueEntry>;
  datasetWeightValue: string;
  datasetWeightPercentage: string;
  attributeGlobalPercentages: Record<string, string>;
  isAllAttributeWeightZero: boolean;
  isAllDatasetWeightZero: boolean;
  datasetIsIncludedScoring: boolean;
  dispatchWeightSettings: React.Dispatch<WeightSettingsAction>;
  selectedScoringOption: ScoringOptions | null;
  /**
   * When true, render the wide advanced table (Mean/Stdv columns + headers).
   * When false, render the compact layout used in the simple settings view.
   */
  showAdvancedColumns: boolean;
  /**
   * When true, the Mean/Stdv inputs surface their red-border errors. Only set
   * after the user attempts to Save in advanced mode — never on first open.
   */
  showAdvancedValidationErrors: boolean;
  /**
   * When true, red-borders the whole dataset row to flag it as a newly-added
   * dataset whose addition auto-adjusted the saved weights.
   */
  isWeightAdjusted?: boolean;
}

const DatasetAttributeItem = ({
  dataset,
  datasetAttributesWeights,
  datasetWeightValue,
  datasetWeightPercentage,
  attributeGlobalPercentages,
  isAllAttributeWeightZero,
  isAllDatasetWeightZero,
  datasetIsIncludedScoring,
  dispatchWeightSettings,
  selectedScoringOption,
  showAdvancedColumns,
  showAdvancedValidationErrors,
  isWeightAdjusted = false
}: DatasetAttributeItemProps) => {
  const [hasBlurredAdvancedInput, setHasBlurredAdvancedInput] = useState(false);
  const datasetId = dataset.data.id;
  const isPoiDataset = dataset.properties.spatialType === "poi";
  const { isAttributeWeightToggleEnabled } = useAppFeatures();
  const primaryPoiAttribute = getPrimaryPoiAttribute(dataset.categories);
  const fallbackPoiAttributeId = Object.keys(datasetAttributesWeights)[0] ?? "";
  const primaryPoiAttributeId = primaryPoiAttribute
    ? getDatasetAttributeScoringId(primaryPoiAttribute)
    : fallbackPoiAttributeId;

  const datasetCategories = dataset.categories.reduce<
    Record<string, DatasetAttributeSchema[][number]>
  >((acc, category) => {
    acc[category.id] = category;
    return acc;
  }, {});

  // All update semantics live in weightSettingsReducer — the handlers only
  // translate UI events into semantic actions.
  const handleUpdateDatasetWeightValue = (value: string, isPOI?: boolean) => {
    dispatchWeightSettings({
      type: "dataset/setWeight",
      datasetId,
      value,
      isPoi: isPOI,
      primaryAttributeId: primaryPoiAttributeId
    });
  };

  const handleUpdateWeightValue = (attributeId: string, value: string) => {
    dispatchWeightSettings({
      type: "attribute/setWeight",
      datasetId,
      attributeId,
      value
    });
  };

  const handleUpdateNegativeImpact = (
    attributeId: string,
    checked: boolean
  ) => {
    dispatchWeightSettings({
      type: "attribute/setNegativeImpact",
      datasetId,
      attributeId,
      negativeImpact: checked
    });
  };

  const handleToggleDataset = (checked: boolean) => {
    dispatchWeightSettings({
      type: "dataset/toggleInclude",
      datasetId,
      included: checked,
      isPoi: isPoiDataset,
      primaryAttributeId: primaryPoiAttributeId
    });
  };

  const handleToggleAttribute = (attributeId: string, checked: boolean) => {
    dispatchWeightSettings({
      type: "attribute/toggleInclude",
      datasetId,
      attributeId,
      included: checked
    });
  };

  const handleUpdateZScore = (
    attributeId: string,
    change: { logMean?: string; logStdDev?: string }
  ) => {
    dispatchWeightSettings({
      type: "attribute/setZScore",
      datasetId,
      attributeId,
      ...change
    });
  };

  const zScoreOf = (attributeId: string) =>
    datasetAttributesWeights[attributeId]?.z_score ?? {
      log_mean: "",
      log_std_dev: ""
    };

  const shouldShowAdvancedValidationErrors =
    showAdvancedValidationErrors || hasBlurredAdvancedInput;

  const handleAdvancedInputBlur = () => {
    setHasBlurredAdvancedInput(true);
  };

  const getAdvancedValidationMessage = (attributeId: string) => {
    const zScore = zScoreOf(attributeId);
    return getAdvancedNormalizationErrorMessage({
      logMean: zScore.log_mean,
      logStdDev: zScore.log_std_dev
    });
  };

  const getAdvancedValidationMessages = (attributeIds: string[]) => {
    if (
      !showAdvancedColumns ||
      !shouldShowAdvancedValidationErrors ||
      !datasetIsIncludedScoring
    ) {
      return [];
    }

    return Array.from(
      new Set(
        attributeIds
          .filter(Boolean)
          .filter((attributeId) =>
            getIsIncludedScoring(datasetAttributesWeights[attributeId])
          )
          .map(getAdvancedValidationMessage)
          .filter((message): message is string => Boolean(message))
      )
    );
  };

  const advancedValidationMessages = getAdvancedValidationMessages(
    isPoiDataset
      ? [primaryPoiAttributeId]
      : Object.keys(datasetAttributesWeights)
  );

  const categoriesContent = Object.keys(datasetAttributesWeights).map((id) => {
    const attributeName =
      dataset.properties.spatialType === "poi"
        ? "Total POI Count" // For POI datasets, only show 1 attribute that can be assigned weight called "Total POI Count"
        : (datasetCategories[id]?.attribute_name ?? id);
    const attrIncluded =
      datasetIsIncludedScoring &&
      getIsIncludedScoring(datasetAttributesWeights[id]);
    const percentageDisplay = attrIncluded
      ? attributeGlobalPercentages[id].toString().replace(/\.00$/, "")
      : "0";

    const weightInput = (
      <InputWithClear
        inputClassName="text-xs"
        className="bg-white max-h-8 rounded-xl text-txtHeading"
        value={getWeightString(datasetAttributesWeights[id])}
        inputMode="decimal"
        dataTestId={`weight-input-${datasetId}-${id}`}
        onChange={(value) => handleUpdateWeightValue(id, value)}
        placeholder="Enter weight value"
        ariaLabel={`Weight value for ${attributeName}`}
        error={isAllAttributeWeightZero}
        disabled={!attrIncluded}
      />
    );

    const attributeToggle = isAttributeWeightToggleEnabled ? (
      <Switch
        aria-label={`include-attribute-${datasetId}-${id}`}
        checked={attrIncluded}
        onCheckedChange={(checked) => handleToggleAttribute(id, checked)}
      />
    ) : null;

    // Compact layout for the simple settings view (no Mean/Stdv columns).
    if (!showAdvancedColumns) {
      return (
        <div key={id}>
          <div className="flex items-center justify-between gap-1">
            <div
              className={twMerge(
                "text-xs text-txtBody mb-2 line-clamp-2",
                !attrIncluded && "opacity-40"
              )}
              title={attributeName}
            >
              {attributeName}
            </div>
            {attributeToggle}
          </div>

          <div className="grid items-center gap-3 grid-cols-[1fr_60px]">
            <div className={twMerge(!attrIncluded && "opacity-40")}>
              {weightInput}
            </div>
            <div className={twMerge(!attrIncluded && "opacity-40")}>
              <WeightPercentage value={percentageDisplay} />
            </div>
          </div>
          {selectedScoringOption === ScoringOptionsEnum.NORMALIZED && (
            <div className={twMerge(!attrIncluded && "opacity-40")}>
              <Checkbox
                inputClassName="size-4"
                className="mt-1.5 text-xs text-txtBody"
                label="Negative impact"
                onChange={(checked) => handleUpdateNegativeImpact(id, checked)}
                checked={Boolean(
                  getNegativeImpactValue(datasetAttributesWeights[id])
                )}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={id} className={twMerge(ATTRIBUTE_GRID, "pb-2")}>
        <div
          className={twMerge(
            "text-xs text-txtHeading line-clamp-2 font-semibold",
            !attrIncluded && "opacity-40"
          )}
          title={attributeName}
        >
          {attributeName}
        </div>

        <div className={twMerge(!attrIncluded && "opacity-40")}>
          {weightInput}
        </div>

        <div className={twMerge(!attrIncluded && "opacity-40")}>
          <WeightPercentage value={percentageDisplay} plain />
        </div>

        <div
          className={twMerge(
            "flex justify-center",
            !attrIncluded && "opacity-40"
          )}
        >
          {selectedScoringOption === ScoringOptionsEnum.NORMALIZED && (
            <Checkbox
              inputClassName="size-4"
              onChange={(checked) => handleUpdateNegativeImpact(id, checked)}
              checked={Boolean(
                getNegativeImpactValue(datasetAttributesWeights[id])
              )}
            />
          )}
        </div>

        <AdvancedSettingInputs
          disabled={!attrIncluded}
          onBlur={handleAdvancedInputBlur}
          logMean={zScoreOf(id).log_mean}
          logStdDev={zScoreOf(id).log_std_dev}
          logMeanError={
            shouldShowAdvancedValidationErrors &&
            attrIncluded &&
            isLogMeanInvalid(zScoreOf(id).log_mean)
          }
          logStdDevError={
            shouldShowAdvancedValidationErrors &&
            attrIncluded &&
            isLogStdDevInvalid(zScoreOf(id).log_std_dev)
          }
          onChangeLogMean={(value) =>
            handleUpdateZScore(id, { logMean: value })
          }
          onChangeLogStdDev={(value) =>
            handleUpdateZScore(id, { logStdDev: value })
          }
        />

        <div className="flex justify-end">{attributeToggle}</div>
      </div>
    );
  });

  if (isPoiDataset) {
    const poiPercentageDisplay = datasetIsIncludedScoring
      ? datasetWeightPercentage?.toString().replace(/\.00$/, "")
      : "0";

    return (
      <div
        className={twMerge(
          "flex flex-col w-full flex-1",
          showAdvancedColumns ? "pt-4 pb-2" : "p-0"
        )}
      >
        <div className="flex items-center gap-1 px-4 pt-4">
          <DatasetTypeIcon type={dataset.data.spatial_aggregation_type} />

          <Tooltip content={dataset.data.display_name} position="top-left">
            <span
              className={twMerge(
                "text-sm text-txtHeading font-semibold line-clamp-1 min-w-0 break-all",
                !datasetIsIncludedScoring && "opacity-40"
              )}
            >
              {dataset.data.display_name}
            </span>
          </Tooltip>

          {isAttributeWeightToggleEnabled && (
            <Switch
              aria-label={`include-dataset-${datasetId}`}
              checked={datasetIsIncludedScoring}
              onCheckedChange={handleToggleDataset}
              className="ml-auto"
            />
          )}
        </div>

        <div
          className={twMerge(
            showAdvancedColumns
              ? "flex w-full items-center gap-3 px-3"
              : "grid grid-cols-[1fr_60px] w-full items-center gap-3 px-4",
            "py-2",
            !datasetIsIncludedScoring && "opacity-40"
          )}
        >
          <div
            className={twMerge(
              showAdvancedColumns && "min-w-0 flex-1 max-w-[682px]"
            )}
          >
            <InputWithClear
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              inputClassName="text-xs"
              className="bg-white max-h-8 rounded-xl text-txtHeading"
              value={datasetWeightValue?.toString()}
              onChange={(value) => handleUpdateDatasetWeightValue(value, true)}
              inputMode="decimal"
              dataTestId={`poi-weight-input-${datasetId}`}
              error={isAllDatasetWeightZero}
              disabled={!datasetIsIncludedScoring}
            />
          </div>
          <WeightPercentage value={poiPercentageDisplay} />
          {showAdvancedColumns && (
            <AdvancedSettingInputs
              disabled={!datasetIsIncludedScoring}
              onBlur={handleAdvancedInputBlur}
              className="flex-1"
              logMean={zScoreOf(primaryPoiAttributeId).log_mean}
              logStdDev={zScoreOf(primaryPoiAttributeId).log_std_dev}
              logMeanError={
                shouldShowAdvancedValidationErrors &&
                datasetIsIncludedScoring &&
                isLogMeanInvalid(zScoreOf(primaryPoiAttributeId).log_mean)
              }
              logStdDevError={
                shouldShowAdvancedValidationErrors &&
                datasetIsIncludedScoring &&
                isLogStdDevInvalid(zScoreOf(primaryPoiAttributeId).log_std_dev)
              }
              onChangeLogMean={(value) =>
                handleUpdateZScore(primaryPoiAttributeId, { logMean: value })
              }
              onChangeLogStdDev={(value) =>
                handleUpdateZScore(primaryPoiAttributeId, { logStdDev: value })
              }
            />
          )}
        </div>
        {selectedScoringOption === ScoringOptionsEnum.NORMALIZED && (
          <div className={twMerge(!datasetIsIncludedScoring && "opacity-40")}>
            <Checkbox
              inputClassName="size-4"
              className="mt-1.5 text-xs text-txtBody px-4"
              label="Negative impact"
              onChange={(checked) =>
                handleUpdateNegativeImpact(primaryPoiAttributeId, checked)
              }
              checked={Boolean(
                getNegativeImpactValue(
                  datasetAttributesWeights[primaryPoiAttributeId]
                )
              )}
            />
          </div>
        )}
        <AdvancedValidationMessages
          messages={advancedValidationMessages}
          className="px-4 pt-1"
        />
      </div>
    );
  }

  const datasetPercentageDisplay = datasetIsIncludedScoring
    ? datasetWeightPercentage?.toString().replace(/\.00$/, "")
    : "0";

  return (
    <AccordionItem
      value={dataset.data.id}
      className={twMerge("flex flex-col w-full flex-1")}
    >
      <div
        className={twMerge(
          "flex flex-row items-center pt-4",
          showAdvancedColumns ? "px-3" : "px-4"
        )}
      >
        <div className="flex items-center gap-1 mr-auto">
          <DatasetTypeIcon type={dataset.data.spatial_aggregation_type} />
          <Tooltip content={dataset.data.display_name} position="top-left">
            <span
              className={twMerge(
                "text-sm text-txtHeading font-semibold line-clamp-1 min-w-0 break-all",
                !datasetIsIncludedScoring && "opacity-40"
              )}
            >
              {dataset.data.display_name}
            </span>
          </Tooltip>
        </div>
        {isAttributeWeightToggleEnabled && (
          <Switch
            aria-label={`include-dataset-${datasetId}`}
            checked={datasetIsIncludedScoring}
            onCheckedChange={handleToggleDataset}
            className="mr-2"
          />
        )}

        <MapMenuItemControl
          tooltipContent={
            <div>
              <p className="font-semibold">Negative Impact</p>
              <p>
                Set weight at dataset level to <br /> distribute equally across
                attributes
              </p>
            </div>
          }
          tooltipPosition="top-left"
          className="cursor-pointer w-5 h-5"
        >
          <InformationIcon className="fill-dim-gray" />
        </MapMenuItemControl>
      </div>

      <div
        className={twMerge(
          "flex flex-row items-center ",
          showAdvancedColumns ? "px-3" : "px-4"
        )}
      >
        <div className="flex flex-col gap-3 w-full pt-2 mr-3">
          <div
            className={twMerge(
              "flex-1 grid grid-cols-[1fr_60px] w-full items-center gap-3",
              !datasetIsIncludedScoring && "opacity-40"
            )}
          >
            <InputWithClear
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              inputClassName="text-xs"
              className="bg-white max-h-8 rounded-xl text-txtHeading"
              value={datasetWeightValue?.toString()}
              onChange={(value) => handleUpdateDatasetWeightValue(value)}
              inputMode="decimal"
              error={isAllDatasetWeightZero}
              disabled={!datasetIsIncludedScoring}
            />
            <WeightPercentage value={datasetPercentageDisplay} />
          </div>
        </div>
        <AccordionTrigger className="w-auto px-0" />
      </div>

      <AccordionContent
        className={twMerge(
          "flex flex-col py-2 px-3 gap-2 mx-4 bg-white-smoke border border-whisper rounded-xl mt-2 divide-y divide-y-reverse divide-[#EEEEEE]",
          showAdvancedColumns && "mx-3 overflow-x-auto custom-scrollbar",
          isWeightAdjusted && "border border-cinnabar rounded-xl p-2"
        )}
      >
        {showAdvancedColumns && (
          <div className={twMerge(ATTRIBUTE_GRID, "text-xs text-txtBody")}>
            <span>Attribute</span>
            <span>Weight</span>
            <span className="text-center">%</span>
            <span className="text-center whitespace-nowrap">
              Negative Impact
            </span>
            <div className="flex items-center gap-2">
              <span className="flex-1 whitespace-nowrap">Mean Value</span>
              <span className="flex-1 whitespace-nowrap">
                Standard Deviation
              </span>
            </div>
            <span />
          </div>
        )}
        {categoriesContent}
      </AccordionContent>
      <AdvancedValidationMessages
        messages={advancedValidationMessages}
        className={twMerge(showAdvancedColumns ? "px-3" : "px-4", "pt-1")}
      />
    </AccordionItem>
  );
};

export default DatasetAttributeItem;
